import {isFeatureEnabled} from '@github-ui/feature-flags'
import {
  type FeedbackRef,
  frownIcon,
  frustratedIcon,
  grinIcon,
  neutralIcon,
  smileIcon,
  UserFeedback,
} from '@github-ui/user-feedback'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {RefObject} from 'react'

const MAX_FEEDBACK_LENGTH = 2000

const ratingOptions = [
  {
    name: 'Hate',
    value: 1 as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Dislike',
    value: 2 as const,
    icon: frownIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Neutral',
    value: 3 as const,
    icon: neutralIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'Like',
    value: 4 as const,
    icon: smileIcon,
    color: 'verySatisfied' as const,
  },
  {
    name: 'Love',
    value: 5 as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type IssueFeedbackDialogRef = FeedbackRef<Rating>

type UserFeedbackEvent = {
  subject?: string
  rating: Rating | null
  content?: string
  hostname: string
  path?: string
}

export interface IssueFeedbackDialogProps {
  title?: string
  subject?: string
  returnFocusRef?: RefObject<HTMLElement | null>
  ref?: React.Ref<IssueFeedbackDialogRef>
}

export function IssueFeedbackDialog({
  ref,
  title = 'Give feedback on Issues',
  subject = 'issues_feedback',
  returnFocusRef,
}: IssueFeedbackDialogProps) {
  const showFeedback = isFeatureEnabled('issues_react_ui_feedback')

  const onSubmit = async (rating: Rating | null, text: string): Promise<string[]> => {
    const errors = []

    if (rating == null) {
      errors.push('Please select a rating.')
    }

    if (text.length > MAX_FEEDBACK_LENGTH) {
      errors.push(`Please keep your feedback within ${MAX_FEEDBACK_LENGTH} characters or less.`)
    }

    if (errors.length > 0) {
      return errors
    }

    const body: UserFeedbackEvent = {
      subject,
      rating,
      content: text,
      hostname: window.location.hostname,
      path: window.location.pathname,
    }

    try {
      const response = await verifiedFetchJSON('/issues/feedback', {method: 'POST', body})
      if (!response.ok) {
        errors.push('An error occurred while submitting your feedback.')
      }
    } catch {
      errors.push('An error occurred while submitting your feedback.')
    }

    return errors
  }

  if (!showFeedback) {
    return null
  }

  return (
    <UserFeedback ref={ref} title={title} options={ratingOptions} onSubmit={onSubmit} returnFocusRef={returnFocusRef} />
  )
}

try{ IssueFeedbackDialog.displayName ||= 'IssueFeedbackDialog' } catch {}